import {
  Box,
  Image,
  useTheme,
  Code,
  VStack,
  Badge,
  Flex,
  IconButton,
} from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableTitle,
} from 'cj-common-react';
import { ChatCJLogo } from '../CJLogo';
import StyledLink from './StyledLink';
import { ReactComponent as ThumbsUpIcon } from '../../logos/thumbsUpIcon.svg';
import { ReactComponent as ThumbsUpIconGreen } from '../../logos/thumbsUpIconGreen.svg';
import { ReactComponent as ThumbsDownIcon } from '../../logos/thumbsDownIcon.svg';
import { ReactComponent as ThumbsDownIconRed } from '../../logos/thumbsDownIconRed.svg';

import { USER_FEEDBACK_TYPES, MESSAGE_DATA_TYPES } from '../../config';
import LoadingDots from './LoadingDots';

const { SQL_DATA, STRING_DATA, JSON_DATA, RELEVANT_SEARCH_PARAMETERS } =
  MESSAGE_DATA_TYPES;

const Message = ({
  messageData,
  userImage,
  userName,
  openPositiveFeedbackModal,
  openNegativeFeedbackModal,
  animate,
}) => {
  const theme = useTheme();
  const handleThumbsUpClick = () => {
    openPositiveFeedbackModal(messageData);
  };

  const handleThumbsDownClick = () => {
    openNegativeFeedbackModal(messageData);
  };

  const userImageBox = () => {
    return (
      <Box pr={4} data-testid='user-image-box'>
        {userImage ? (
          <Image
            boxSize='36px'
            mr='6px'
            borderRadius='4px'
            src={userImage}
            alt={userName}
          />
        ) : (
          <ChatCJLogo width='40px' height='40px' />
        )}
      </Box>
    );
  };

  const formatSql = (sql) => {
    return sql.replace(/(FROM|WHERE|GROUP BY|ORDER BY|LIMIT)/g, '\n$1'); //Avoid SELECT b/c it starts the statement
  };

  const safelyParseJson = (str) => {
    try {
      const parsed = JSON.parse(str);
      if (typeof parsed === 'string') {
        return safelyParseJson(parsed);
      } else {
        return parsed;
      }
    } catch (error) {
      console.debug('Error while parsing JSON: ', error);
      return null;
    }
  };

  const flattenRelevantDocuments = (data) => {
    let results = [];
    for (const item of data) {
      const [
        {
          pageContent,
          metadata: { columnName },
        },
        similarityScore,
      ] = item;
      results.push({ columnName, pageContent, similarityScore });
    }
    return results;
  };

  const renderDataBlock = () => {
    let data = safelyParseJson(messageData.data);
    if (!data[0]) {
      console.debug('no result for: ', JSON.stringify(data, null, 2));
      return <Code>No result</Code>;
    }
    if (messageData.text === 'Relevant documents:') {
      data = flattenRelevantDocuments(data);
    }

    const jsonKeys = Object.keys(data[0]);
    return (
      <Table variant='simple'>
        <TableHead>
          <TableRow /*sticky*/>
            {jsonKeys.map((key, index) => (
              <TableTitle
                padding={2}
                fontFamily='arial'
                fontWeight='bold'
                backgroundColor={theme.colors.explorerTableHeaderColor}
                key={index}
                title={key}
              />
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((item, index) => (
            <TableRow key={index}>
              {jsonKeys.map((key, subIndex) => (
                <TableCell
                  padding={2}
                  fontFamily='arial'
                  key={subIndex}
                  title={
                    typeof item[key] === 'object'
                      ? JSON.stringify(item[key])
                      : item[key]
                  }
                />
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const renderRelevantSearchParameters = (messageData) => {
    const searchArguments = safelyParseJson(
      messageData.relevantSearchParameters,
    );

    if (!searchArguments) {
      return null;
    }

    return (
      <Box mt={2}>
        <strong>Searching for doctors using the following parameters:</strong>
        {Object.keys(searchArguments).map((key, index) => (
          <Box key={index}>
            {key}:
            {Array.isArray(searchArguments[key]) ? (
              searchArguments[key].map((value, subIndex) => (
                <Badge
                  key={subIndex}
                  variant='outline'
                  colorScheme='blue'
                  ml={2}
                >
                  {typeof value === 'object' && value !== null
                    ? `${value.pageContent}`
                    : value}
                </Badge>
              ))
            ) : (
              <Badge variant='outline' colorScheme='blue' ml={2}>
                {searchArguments[key]}
              </Badge>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  const renderMessageData = () => {
    switch (messageData.dataType) {
      case SQL_DATA:
        return <Code>{formatSql(messageData.data)}</Code>;
      case STRING_DATA:
        return <Code>{messageData.data}</Code>;
      case JSON_DATA:
        return renderDataBlock();
      case RELEVANT_SEARCH_PARAMETERS:
        return renderRelevantSearchParameters(messageData);
      default:
        return null;
    }
  };

  const renderFeedbackIcons = () => {
    if (
      userName !== 'CareJourney' ||
      messageData.dataType === RELEVANT_SEARCH_PARAMETERS ||
      messageData.streaming
    ) {
      return;
    }
    return (
      <Flex justifyContent='flex-end'>
        {messageData.feedbackType === USER_FEEDBACK_TYPES.POSITIVE ? (
          <ThumbsUpIconGreen width='24px' height='24px' />
        ) : messageData.feedbackType === USER_FEEDBACK_TYPES.NEGATIVE ? (
          <ThumbsDownIconRed width='24px' height='24px' />
        ) : (
          <>
            <IconButton
              icon={<ThumbsUpIcon />}
              size='sm'
              variant='ghost'
              aria-label='Thumbs Up'
              mr='1'
              onClick={handleThumbsUpClick}
            />
            <IconButton
              icon={<ThumbsDownIcon />}
              size='sm'
              variant='ghost'
              aria-label='Thumbs Down'
              onClick={handleThumbsDownClick}
            />
          </>
        )}
      </Flex>
    );
  };

  return (
    <Box
      my={2}
      p={3}
      m={2}
      borderRadius='10px'
      // boxShadow='md'
      display='flex'
      flexDirection='column'
      backgroundColor={
        userName !== 'CareJourney'
          ? theme.colors.bgGrey2
          : 'rgba(235, 247, 252, 0.75)'
      }
      fontSize='1rem'
    >
      <Flex flexGrow={1}>
        {userImageBox()}
        {animate && <LoadingDots />}
        <VStack align='left' spacing={0} flex='1'>
          <Box mt={2} className='markdown-content'>
            <ReactMarkdown components={{ a: StyledLink }}>
              {messageData.text}
            </ReactMarkdown>
          </Box>
          <Box mt={2} whiteSpace='pre-wrap'>
            {renderMessageData()}
          </Box>
          <Box mt={2} whiteSpace='pre-wrap'>
            {messageData.postText}
          </Box>
        </VStack>
      </Flex>
      {renderFeedbackIcons()}
    </Box>
  );
};

export default Message;
